/**
 * E-58 social sign-in buttons (F-375 / F-376 / F-377 — batch 4 S2-S4).
 *
 * Market-gated: social surfaces render only for markets with provisioned
 * Cognito IdPs — the existing email + password and E-039 SAML paths are left
 * untouched.
 *
 * Providers per market:
 *   - Texas: Google (F-375) + Microsoft (F-377, Entra ID via Cognito Hosted
 *     UI — independent of the E-039 SAML flow in Auth/SSOLogin). Apple
 *     (F-376) is hidden until REACT_APP_APPLE_ENABLED === 'true' and sends
 *     the `name` scope only on the sign-up surface (first sign-up).
 *   - UK: Google only (replicated from Texas 2026-10-19 — Microsoft/Apple
 *     are not provisioned on the eu-west-2 pools and stay hidden).
 */
import React from 'react';
import { getOAuthUrl, isAppleEnabled, SocialProvider } from '../../services/cognito';
import { getAppMarket } from '../../services/MarketService';
import { trackLogin, trackSignup } from '../../services/analytics';

export type SocialAuthContext = 'login' | 'signup';

export interface SocialLoginButtonsProps {
  context: SocialAuthContext;
  onRedirect?: (provider: SocialProvider) => void;
}

/** Market gate — markets with provisioned Cognito social IdPs (Texas, UK). */
export const socialLoginEnabled = (): boolean => {
  const market = getAppMarket();
  return market === 'texas' || market === 'uk';
};

const PROVIDER_LABEL: Record<SocialProvider, string> = {
  Google: 'Google',
  Microsoft: 'Microsoft',
  Apple: 'Apple',
};

const PROVIDER_ICON: Record<SocialProvider, string> = {
  Google: 'fab fa-google',
  Microsoft: 'fab fa-microsoft',
  Apple: 'fab fa-apple',
};

export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({ context, onRedirect }) => {
  if (!socialLoginEnabled()) {
    return null;
  }

  const market = getAppMarket();
  const providers: SocialProvider[] =
    market === 'uk' ? ['Google'] : ['Google', 'Microsoft'];
  if (market === 'texas' && isAppleEnabled()) {
    providers.push('Apple');
  }

  const handleClick = (provider: SocialProvider) => {
    // Best-effort funnel tagging for the attempt (consent-gated).
    if (context === 'signup') {
      trackSignup(`social-${provider.toLowerCase()}`);
    } else {
      trackLogin(`social-${provider.toLowerCase()}`);
    }
    onRedirect?.(provider);
    const url = getOAuthUrl(provider, {
      // Apple returns the user's name only on first sign-up, so the name
      // scope travels with the sign-up surface, never with login.
      nameScope: context === 'signup' && provider === 'Apple',
    });
    window.location.href = url;
  };

  return (
    <div className="mt-4" data-testid="social-login-section">
      <div className="flex items-center gap-3 my-3">
        <span className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-500 font-medium">or continue with</span>
        <span className="flex-1 h-px bg-gray-200" />
      </div>
      <div className="grid grid-cols-1 gap-2">
        {providers.map((provider) => (
          <button
            key={provider}
            type="button"
            data-testid={`social-${provider.toLowerCase()}`}
            onClick={() => handleClick(provider)}
            className="flex items-center justify-center gap-2 w-full border border-gray-300 bg-white text-gray-800 rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-gray-50"
          >
            <i className={PROVIDER_ICON[provider]} aria-hidden="true" />
            Continue with {PROVIDER_LABEL[provider]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SocialLoginButtons;